import { CreditCard, Smartphone, Loader2 } from "lucide-react";
import type React from "react";
import { useState } from "react";

import { formatCurrency } from "../../../utils/helpers";
import { Button } from "../../ui/button";
import { Card, CardContent } from "../../ui/card";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { RadioGroup, RadioGroupItem } from "../../ui/radio-group";

interface PaymentFormProps {
  amount: number;
  paymentMethod: "stripe" | "paypal";
  onPaymentMethodChange: (method: "stripe" | "paypal") => void;
  onPaymentSuccess: (paymentData: any) => void;
  isLoading?: boolean;
}

const PaymentForm: React.FC<PaymentFormProps> = ({
  amount,
  paymentMethod,
  onPaymentMethodChange,
  onPaymentSuccess,
  isLoading = false,
}) => {
  const [cardData, setCardData] = useState({
    number: "",
    expiry: "",
    cvc: "",
    name: "",
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Simulate payment processing
    setTimeout(() => {
      onPaymentSuccess({
        paymentMethod,
        transactionId: `txn_${Date.now()}`,
        amount,
      });
    }, 2000);
  };

  const formatCardNumber = (value: string) => {
    const v = value.replace(/\s+/g, "").replace(/[^0-9]/gi, "");
    const matches = v.match(/\d{4,16}/g);
    const match = (matches && matches[0]) || "";
    const parts = [];

    for (let i = 0, len = match.length; i < len; i += 4) {
      parts.push(match.substring(i, i + 4));
    }

    if (parts.length) {
      return parts.join(" ");
    } else {
      return v;
    }
  };

  const formatExpiry = (value: string) => {
    const v = value.replace(/\s+/g, "").replace(/[^0-9]/gi, "");
    if (v.length >= 2) {
      return `${v.substring(0, 2)}/${v.substring(2, 4)}`;
    }
    return v;
  };

  return (
    <form onSubmit={handleSubmit} className='space-y-6'>
      {/* Payment Method Selection */}
      <div className='space-y-3'>
        <Label>Payment Method</Label>
        <RadioGroup
          value={paymentMethod}
          onValueChange={value => onPaymentMethodChange(value as "stripe" | "paypal")}
        >
          <div className='flex items-center space-x-2'>
            <RadioGroupItem value='stripe' id='stripe' />
            <Label htmlFor='stripe' className='flex cursor-pointer items-center gap-2'>
              <CreditCard className='h-4 w-4' />
              Credit/Debit Card
            </Label>
          </div>
          <div className='flex items-center space-x-2'>
            <RadioGroupItem value='paypal' id='paypal' />
            <Label htmlFor='paypal' className='flex cursor-pointer items-center gap-2'>
              <Smartphone className='h-4 w-4' />
              PayPal
            </Label>
          </div>
        </RadioGroup>
      </div>

      {/* Payment Forms */}
      {paymentMethod === "stripe" && (
        <Card>
          <CardContent className='space-y-4 p-4'>
            <div className='space-y-2'>
              <Label htmlFor='cardName'>Cardholder Name</Label>
              <Input
                id='cardName'
                placeholder='John Doe'
                value={cardData.name}
                onChange={e => setCardData({ ...cardData, name: e.target.value })}
                required
              />
            </div>

            <div className='space-y-2'>
              <Label htmlFor='cardNumber'>Card Number</Label>
              <Input
                id='cardNumber'
                placeholder='1234 5678 9012 3456'
                value={cardData.number}
                onChange={e =>
                  setCardData({ ...cardData, number: formatCardNumber(e.target.value) })
                }
                maxLength={19}
                required
              />
            </div>

            <div className='grid grid-cols-2 gap-4'>
              <div className='space-y-2'>
                <Label htmlFor='expiry'>Expiry Date</Label>
                <Input
                  id='expiry'
                  placeholder='MM/YY'
                  value={cardData.expiry}
                  onChange={e => setCardData({ ...cardData, expiry: formatExpiry(e.target.value) })}
                  maxLength={5}
                  required
                />
              </div>
              <div className='space-y-2'>
                <Label htmlFor='cvc'>CVC</Label>
                <Input
                  id='cvc'
                  placeholder='123'
                  value={cardData.cvc}
                  onChange={e =>
                    setCardData({ ...cardData, cvc: e.target.value.replace(/\D/g, "") })
                  }
                  maxLength={4}
                  required
                />
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {paymentMethod === "paypal" && (
        <Card>
          <CardContent className='p-4 text-center'>
            <div className='py-8'>
              <Smartphone className='mx-auto mb-4 h-12 w-12 text-muted-foreground' />
              <p className='text-muted-foreground'>
                You will be redirected to PayPal to complete your payment securely.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Submit Button */}
      <Button type='submit' className='w-full' size='lg' disabled={isLoading}>
        {isLoading && <Loader2 className='mr-2 h-4 w-4 animate-spin' />}
        Pay {formatCurrency(amount)}
      </Button>
    </form>
  );
};

export default PaymentForm;
